import React, { useState } from 'react';
import api from '../services/apiService';
import {
  personDisplayName,
  personRoles,
  ACADEMY_ADMIN_API,
  ACADEMY_ROLE_OPTIONS,
  ACADEMY_ROLE_ADMIN,
} from '../utils/academyAccess';

const emptyNewPerson = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  roles: [ACADEMY_ROLE_ADMIN],
};

const personKey = (person) => `${person.person_type}-${person.person_id}`;

const withRoles = (person, roles) => {
  const next = ACADEMY_ROLE_OPTIONS
    .map((option) => option.id)
    .filter((role) => roles.includes(role));
  const extras = personRoles(person).filter((role) => !ACADEMY_ROLE_OPTIONS.some((option) => option.id === role));
  const combined = [...next, ...extras];
  return {
    ...person,
    roles: combined,
    role: combined.join(', '),
  };
};

const RoleChoices = ({ roles, disabled, onToggle }) => (
  <div className="role-choices">
    {ACADEMY_ROLE_OPTIONS.map((option) => (
      <label key={option.id} className="role-choice">
        <input
          type="checkbox"
          checked={roles.includes(option.id)}
          disabled={disabled}
          onChange={() => onToggle(option.id)}
        />
        <span>
          {option.label}
          <small>{option.detail}</small>
        </span>
      </label>
    ))}
  </div>
);

const AcademyPersonSearch = ({ people = [], onChange, disabled = false, academyId = 0 }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');
  const [newPerson, setNewPerson] = useState(emptyNewPerson);

  const updatePeople = (next) => {
    if (onChange) {
      onChange(next);
    }
  };

  const addPerson = (person) => {
    const key = personKey(person);
    if (people.some((row) => personKey(row) === key)) {
      return;
    }
    const roles = personRoles(person);
    updatePeople([...people, withRoles(person, roles.length ? roles : [ACADEMY_ROLE_ADMIN])]);
  };

  const search = async (value) => {
    setTerm(value);
    if (value.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const academyQuery = academyId ? `&id=${encodeURIComponent(academyId)}` : '';
      const response = await api.get(`${ACADEMY_ADMIN_API}/search_academy_people.php?term=${encodeURIComponent(value.trim())}${academyQuery}`);
      setResults(response.data.people || []);
    } catch (err) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFormError('');
    if (!newPerson.first_name.trim() || !newPerson.last_name.trim() || !newPerson.phone.trim()) {
      setFormError('Enter first name, last name and phone');
      return;
    }
    if (!newPerson.roles.length) {
      setFormError('Choose at least one role');
      return;
    }
    setCreating(true);
    try {
      const response = await api.post(`${ACADEMY_ADMIN_API}/create_academy_person.php`, {
        ...(academyId ? { id: Number(academyId) } : {}),
        first_name: newPerson.first_name.trim(),
        last_name: newPerson.last_name.trim(),
        phone: newPerson.phone.trim(),
        email: newPerson.email.trim(),
        roles: newPerson.roles,
        role: newPerson.roles[0] || '',
      });
      if (!response.data.success || !response.data.person) {
        throw new Error(response.data.message || 'Could not add person');
      }
      addPerson(response.data.person);
      setNewPerson(emptyNewPerson);
      setAddingNew(false);
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Could not add person');
    } finally {
      setCreating(false);
    }
  };

  const toggleRole = (key, role) => {
    updatePeople(people.map((person) => {
      if (personKey(person) !== key) {
        return person;
      }
      const current = personRoles(person);
      const next = current.includes(role)
        ? current.filter((value) => value !== role)
        : [...current, role];
      return withRoles(person, next);
    }));
  };

  const toggleNewRole = (role) => {
    setNewPerson((current) => {
      const roles = current.roles.includes(role)
        ? current.roles.filter((value) => value !== role)
        : [...current.roles, role];
      return { ...current, roles };
    });
  };

  return (
    <div className="people-picker">
      {people.length > 0 && (
        <ul className="assigned-people">
          {people.map((person) => {
            const key = personKey(person);
            const roles = personRoles(person);
            const extras = roles.filter((role) => !ACADEMY_ROLE_OPTIONS.some((option) => option.id === role));
            return (
              <li key={key} className="person-selected">
                <div>
                  <strong>{personDisplayName(person)}</strong>
                  <small>
                    {person.person_label || person.detail || ''}
                    {person.phone ? ` · ${person.phone}` : ''}
                  </small>
                  <RoleChoices
                    roles={roles}
                    disabled={disabled}
                    onToggle={(role) => toggleRole(key, role)}
                  />
                  {extras.length > 0 && (
                    <small>Also saved: {extras.join(', ')}</small>
                  )}
                </div>
                {!disabled && (
                  <button
                    type="button"
                    className="clear-person"
                    onClick={() => updatePeople(people.filter((row) => personKey(row) !== key))}
                  >
                    Remove
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!disabled && (
        <>
          <div className="person-search">
            <input
              type="text"
              value={term}
              onChange={(e) => search(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              placeholder="Search coaches or players"
            />
            {searching && <p className="hint">Searching…</p>}
            {results.length > 0 && (
              <ul className="person-results">
                {results.map((person) => (
                  <li key={personKey(person)}>
                    <button
                      type="button"
                      onClick={() => {
                        addPerson({ ...person, roles: [ACADEMY_ROLE_ADMIN] });
                        setTerm('');
                        setResults([]);
                      }}
                    >
                      <strong>{personDisplayName(person)}</strong>
                      <small>
                        {person.person_label || person.detail || ''}
                        {person.phone ? ` · ${person.phone}` : ''}
                      </small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {addingNew ? (
            <div className="new-person-form" onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}>
              <p className="hint">Creates a coach/manager account if they are not already on First Whistle.</p>
              <div className="field-row">
                <label className="field">
                  First name
                  <input
                    value={newPerson.first_name}
                    onChange={(e) => setNewPerson((current) => ({ ...current, first_name: e.target.value }))}
                  />
                </label>
                <label className="field">
                  Last name
                  <input
                    value={newPerson.last_name}
                    onChange={(e) => setNewPerson((current) => ({ ...current, last_name: e.target.value }))}
                  />
                </label>
              </div>
              <div className="field-row">
                <label className="field">
                  Phone
                  <input
                    value={newPerson.phone}
                    onChange={(e) => setNewPerson((current) => ({ ...current, phone: e.target.value }))}
                  />
                </label>
                <label className="field">
                  Email (optional)
                  <input
                    type="email"
                    value={newPerson.email}
                    onChange={(e) => setNewPerson((current) => ({ ...current, email: e.target.value }))}
                  />
                </label>
              </div>
              <div className="field">
                Roles
                <RoleChoices
                  roles={newPerson.roles}
                  onToggle={toggleNewRole}
                />
              </div>
              {formError && <p className="hint form-error">{formError}</p>}
              <div className="person-actions">
                <button type="button" className="secondary-button" onClick={handleCreate} disabled={creating}>
                  {creating ? 'Adding…' : 'Add person'}
                </button>
                <button
                  type="button"
                  className="clear-person"
                  onClick={() => {
                    setAddingNew(false);
                    setFormError('');
                    setNewPerson(emptyNewPerson);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="clear-person add-new-person" onClick={() => setAddingNew(true)}>
              Person not listed? Add a new person
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default AcademyPersonSearch;
